/**
 * CATALOG TEMPLATES — методы каталога (часть 1: рендер и добавление в смету).
 *
 * Phase 5 modularization (Sprint 6, 2026-05-07).
 * Извлечено из public/js/app.js (DOMAIN: CATALOG TEMPLATES).
 *
 * Включает:
 *   - renderTemplates()                              — рендер сетки карточек услуг
 *   - addServiceFromTemplate(templateId)             — добавить из каталога в основной список
 *                                                      (также роутит в hotels/flights/other по категории — Phase 1 fix)
 *   - addServiceFromTemplateAsOptional(templateId)   — добавить как опциональную услугу
 *   - addServiceFromTemplateToOtherServices(id)      — добавить в «Прочие услуги»
 *
 * Phase 2 fixes:
 *   - service.day = template.defaultDay || 1
 *   - service.markup = template.defaultMarkup || 0
 *
 * Остальные catalog-методы (showCatalogModal, editTemplate, addToCatalog,
 * filterTemplates, renderCategories) пока остаются в app.js — будут извлечены
 * в catalog-modal.js / catalog-categories.js на следующих итерациях.
 *
 * @module app/catalog-templates
 */

// i18n helper: returns translation if loaded, else fallback (EN). Safe to call before module loads.
const _t = (k, fb) => {
    const v = window.i18n?.t?.(k);
    return (v && v !== k) ? v : (fb || k);
};

/**
 * Прикрепляет catalog-template методы к prototype класса.
 * @param {Function} ClassRef - класс ProfessionalQuoteCalculator
 */
export function attachCatalogTemplates(ClassRef) {
    Object.assign(ClassRef.prototype, {
        renderTemplates() {
            const container = document.getElementById('templates-list');
            if (!container) return;

            let filtered = this.templates;

            console.log('renderTemplates: activeCategory =', this.state.activeCategory);
            console.log('renderTemplates: total templates =', this.templates.length);

            if (this.state.activeCategory !== 'all') {
                filtered = filtered.filter(t => t.category === this.state.activeCategory);
                console.log('renderTemplates: filtered templates =', filtered.length, 'for category', this.state.activeCategory);
            }

            container.innerHTML = filtered.map(template => {
                // 🆕 Phase 9: имя поставщика из cache (по supplier_id), fallback на contractor
                const supplierName = (typeof this.getSupplierName === 'function')
                    ? this.getSupplierName(template)
                    : (template.contractor || '');
                return `
                <div class="template-item"
                     onclick="window.QuoteCalc.addServiceFromTemplate('${template.id}')"
                     data-name="${(template.name || '').toLowerCase()}"
                     data-contractor="${(supplierName || '').toLowerCase()}"
                     data-description="${(template.description || '').toLowerCase()}">
                    <div class="template-controls">
                        <button class="template-btn delete" data-text="${_t('app.catalog_templates.delete_label_upper', 'DELETE')}" onclick="event.stopPropagation(); window.QuoteCalc.deleteTemplate('${template.id}')" title="${_t('btn.delete', 'Delete')}">×</button>
                        <button class="template-btn edit" data-text="${_t('app.catalog_templates.edit_label_upper', 'EDIT')}" onclick="event.stopPropagation(); window.QuoteCalc.editTemplate('${template.id}')" title="${_t('btn.edit', 'Edit')}">✎</button>
                    </div>
                    <div class="template-icon">${this.renderIcon(template.icon, '20px')}</div>
                    <div class="template-name">${template.name}</div>
                    ${supplierName ? `<div class="text-xs text-muted">${supplierName}</div>` : ''}
                    <div class="template-price">${template.price}</div>
                    <button class="template-other-services-btn"
                            onclick="event.stopPropagation(); window.QuoteCalc.addServiceFromTemplateToOtherServices('${template.id}')"
                            title="${_t('app.catalog_templates.add_to_other', 'Add to other services')}">
                        📝
                    </button>
                    <button class="template-optional-btn"
                            onclick="event.stopPropagation(); window.QuoteCalc.addServiceFromTemplateAsOptional('${template.id}')"
                            title="${_t('app.catalog_templates.add_to_optional', 'Add to optional services')}">
                        <i data-lucide="clipboard-plus" style="width: 12px; height: 12px;"></i>
                    </button>
                </div>
            `;
            }).join('');
            lucide.createIcons();
        },

        addServiceFromTemplate(templateId) {
            const template = this.templates.find(t => t.id === templateId);
            if (!template || template.editing) return;

            // Track category usage (локальная статистика для сортировки)
            if (template.category) {
                this.trackCategoryUsage(template.category);
            }

            // Track template usage (локальная статистика для сортировки услуг)
            this.trackTemplateUsage(templateId);

            // Track global usage (глобальная статистика для аналитики)
            this.trackGlobalUsage(templateId, template.name, template.category);

            // Visual feedback
            const templateElement = document.querySelector(`[data-template-id="${templateId}"]`);
            if (templateElement) {
                templateElement.classList.add('loading');
            }

            const service = {
                id: Date.now().toString(),
                name: template.name,
                description: template.description || template.name,
                // 🆕 Group 2: для отелей берём день из template.defaultDay (если задан),
                //   иначе 1. Поле остаётся редактируемым в карточке.
                day: template.defaultDay || 1,
                price: template.price,
                quantity: 1,
                // 🆕 Group 2: дефолтная наценка из услуги (редактируется в карточке услуги).
                markup: template.defaultMarkup || 0,
                contractor: template.contractor || '',
                excludeFromMarkup: false,
                region: template.region || this.currentRegion, // Берем регион из услуги
                // Checklist fields
                done: false,        // Забронировано
                paid: false,        // Оплачено
                comment: ''         // Комментарий менеджера
            };
            // 🆕 Phase 9: пропагируем supplier_id из услуги
            if (template.supplier_id) service.supplier_id = template.supplier_id;

            // Validate before adding
            const errors = this.validateService(service);
            if (errors.length > 0) {
                this.showNotification(errors.join(', '), true);
                if (templateElement) templateElement.classList.remove('loading');
                return;
            }

            // 🔧 FIX (P0): автоматическая сортировка работает по ID категории И её отображаемому имени.
            //   Раньше использовался только ID, но кириллические категории получают ID вида
            //   "cat_1730894732145" (timestamp), и матчинг по 'перелет'/'отел' никогда не срабатывал.
            const categoryId = (template.category || '').toLowerCase();
            const categoryObj = (this.categories || []).find(c => c.id === template.category);
            const categoryName = (categoryObj?.name || '').toLowerCase();
            const cat = `${categoryId} ${categoryName}`;  // объединяем для одного includes

            const isHotelCategory = cat.includes('accommodation') || cat.includes('отел') ||
                cat.includes('hotel') || cat.includes('гостиниц') || cat.includes('lodging');
            const isFlightCategory = cat.includes('flight') || cat.includes('перелет') ||
                cat.includes('перелёт') || cat.includes('полет') || cat.includes('полёт') ||
                cat.includes('авиа') || cat.includes('airfare');

            if (isHotelCategory) {
                // Добавляем в отели
                this.state.hotels.unshift(service);
                this.renderHotels();
                this.showNotification(_t('app.catalog_templates.hotel_added', 'Hotel added'));
            } else if (isFlightCategory) {
                // Добавляем в перелёты
                this.state.flights.unshift(service);
                this.renderFlights();
                this.showNotification(_t('app.catalog_templates.flight_added', 'Flight added'));
            } else if (service.noDayAssignment) {
                // Услуга отмечена как "без дня" - в прочие
                this.state.otherServices.unshift(service);
                this.renderOtherServices();
                this.showNotification(_t('app.catalog_templates.service_added_to_other', 'Service added to other'));
            } else {
                // Добавляем в обычные услуги
                this.state.services.unshift(service);
                this.renderServices();
                this.showNotification(_t('app.catalog_templates.service_added', 'Service added'));
            }

            this.scheduleUpdate();
            this.markQuoteAsUnsaved();

            // Remove loading state
            setTimeout(() => {
                if (templateElement) templateElement.classList.remove('loading');
            }, 200);
        },

        addServiceFromTemplateAsOptional(templateId) {
            const template = this.templates.find(t => t.id === templateId);
            if (!template || template.editing) return;

            // Track category usage (локальная статистика для сортировки)
            if (template.category) {
                this.trackCategoryUsage(template.category);
            }

            // Track template usage (локальная статистика для сортировки услуг)
            this.trackTemplateUsage(templateId);

            // Track global usage (глобальная статистика для аналитики)
            this.trackGlobalUsage(templateId, template.name, template.category);

            const service = {
                id: Date.now().toString(),
                name: template.name,
                description: template.description || template.name,
                day: template.defaultDay || 1, // 🆕 Group 2
                price: template.price,
                quantity: 1,
                markup: template.defaultMarkup || 0, // 🆕 Group 2
                contractor: template.contractor || '',
                excludeFromMarkup: false,
                fullProfit: false,
                region: template.region || this.currentRegion, // Берем регион из услуги
                // Checklist fields
                done: false,        // Забронировано
                paid: false,        // Оплачено
                comment: ''         // Комментарий менеджера
            };
            // 🆕 Phase 9: пропагируем supplier_id из услуги
            if (template.supplier_id) service.supplier_id = template.supplier_id;

            // Validate before adding
            const errors = this.validateService(service);
            if (errors.length > 0) {
                this.showNotification(errors.join(', '), true);
                return;
            }

            // Add to optional services array
            this.state.optionalServices.unshift(service);
            this.renderOptionalServices();
            this.markQuoteAsUnsaved();

            this.showNotification(_t('app.catalog_templates.service_added_to_optional', 'Service added to optional'));
        },

        addServiceFromTemplateToOtherServices(templateId) {
            const template = this.templates.find(t => t.id === templateId);
            if (!template || template.editing) return;

            // Track category usage (локальная статистика для сортировки)
            if (template.category) {
                this.trackCategoryUsage(template.category);
            }

            // Track template usage (локальная статистика для сортировки услуг)
            this.trackTemplateUsage(templateId);

            // Track global usage (глобальная статистика для аналитики)
            this.trackGlobalUsage(templateId, template.name, template.category);

            const service = {
                id: Date.now().toString(),
                name: template.name,
                description: template.description || template.name,
                day: template.defaultDay || 1, // 🆕 Group 2
                price: template.price,
                quantity: 1,
                markup: template.defaultMarkup || 0, // 🆕 Group 2
                contractor: template.contractor || '',
                excludeFromMarkup: false,
                fullProfit: false,
                region: template.region || this.currentRegion, // Берем регион из услуги
                // Checklist fields
                done: false,        // Забронировано
                paid: false,        // Оплачено
                comment: ''         // Комментарий менеджера
            };
            // 🆕 Phase 9: пропагируем supplier_id из услуги
            if (template.supplier_id) service.supplier_id = template.supplier_id;

            // Validate before adding
            const errors = this.validateService(service);
            if (errors.length > 0) {
                this.showNotification(errors.join(', '), true);
                return;
            }

            // Add to other services array
            this.state.otherServices.unshift(service);
            this.renderOtherServices();
            this.updateCalculations();
            this.markQuoteAsUnsaved();

            this.showNotification(_t('app.catalog_templates.service_added_to_other_services', 'Service added to other services'));
        },
    });
}
